import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { authFetch } from "../../App";
import { ArrowLeft, AlertTriangle, Wrench, Sparkles, ImageIcon, ShieldAlert, Info, FileText } from "lucide-react";

const API = "/api";

const PRIORITY_STYLE = {
  niedrig:  { dot: "#22c55e", label: "niedrig",  bg: "bg-emerald-500/10 text-emerald-200 border-emerald-400/30" },
  mittel:   { dot: "#f59e0b", label: "mittel",   bg: "bg-amber-500/10 text-amber-200 border-amber-400/30" },
  hoch:     { dot: "#ef4444", label: "hoch",     bg: "bg-red-500/10 text-red-200 border-red-400/30" },
  kritisch: { dot: "#7c1d1d", label: "kritisch", bg: "bg-red-700/20 text-red-100 border-red-500/40" },
};

export default function HpsPruefung() {
  const { inspectionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${API}/hps/inspections/${inspectionId}/full`);
        if (r.ok) setData(await r.json());
      } finally { setLoading(false); }
    })();
  }, [inspectionId]);

  if (loading) return <div className="text-white/60 text-sm py-20 text-center">Lade Prüfung …</div>;
  if (!data?.inspection) return <div className="text-red-300 text-sm">Prüfung nicht gefunden</div>;

  const { inspection, object, customer, systems, defects, ai_assessments, proposals, photos, report } = data;

  // Prioritätenampel
  const priorityCounts = defects.reduce((acc, d) => {
    acc[d.priority] = (acc[d.priority] || 0) + 1; return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="hps-pruefung-detail">
      <Link to="/app/hps-anlagencheck/objekte" className="inline-flex items-center gap-1 text-xs text-white/55 hover:text-white">
        <ArrowLeft className="w-3 h-3" /> zurück zur Übersicht
      </Link>

      {/* Header */}
      <div className="rounded-xl border border-white/10 p-5"
           style={{ background: "rgba(255,255,255,0.04)" }}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-xl md:text-2xl font-semibold text-white">{inspection.title}</h1>
              {inspection.is_demo && <span className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/15 border border-amber-400/30 rounded px-1.5 py-0.5">Demo</span>}
            </div>
            <div className="text-sm text-white/60">
              {customer?.name} · {object?.name}
            </div>
            <div className="text-xs text-white/45 mt-1">
              Datum: {inspection.inspection_date} · Prüfer: {inspection.inspector_name || "—"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Status</div>
            <span className="text-xs text-amber-200 bg-amber-500/15 border border-amber-400/30 rounded px-2 py-1">
              {inspection.status === "in_pruefung" ? "in Prüfung" : inspection.status}
            </span>
          </div>
        </div>
        {inspection.summary && (
          <div className="mt-4 pt-4 border-t border-white/5 text-sm text-white/75 leading-relaxed whitespace-pre-wrap">
            {inspection.summary}
          </div>
        )}
      </div>

      {/* Prioritätenampel */}
      <div className="grid grid-cols-3 gap-3">
        <PriorityTile color="#ef4444" label="Hoch / Kritisch" count={(priorityCounts.hoch || 0) + (priorityCounts.kritisch || 0)} testid="hps-prio-hoch" />
        <PriorityTile color="#f59e0b" label="Mittel" count={priorityCounts.mittel || 0} testid="hps-prio-mittel" />
        <PriorityTile color="#22c55e" label="Niedrig" count={priorityCounts.niedrig || 0} testid="hps-prio-niedrig" />
      </div>

      {/* Anlagenbereiche mit Mängeln + KI + Sanierung */}
      <div className="space-y-5">
        {systems.map((sys) => {
          const sysDefects = defects.filter((d) => d.system_id === sys.id);
          const sysAi = ai_assessments.find((a) => a.system_id === sys.id);
          const sysProposals = proposals.filter((p) => p.system_id === sys.id);
          const sysPhotos = photos.filter((p) => p.system_id === sys.id);
          return (
            <div key={sys.id} className="rounded-xl border border-white/10 p-5"
                 style={{ background: "rgba(255,255,255,0.04)" }}
                 data-testid={`hps-system-${sys.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <div>
                  <h2 className="text-base md:text-lg font-medium text-white">{sys.name}</h2>
                  <div className="text-xs text-white/50">{sys.location || "—"}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-blue-200 bg-blue-500/15 border border-blue-400/30 rounded px-1.5 py-0.5">{sys.catalog_key}</span>
              </div>

              {/* Mängel */}
              {sysDefects.length > 0 && (
                <div className="mb-4">
                  <div className="text-[11px] uppercase tracking-wider text-white/40 font-medium mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Mängel ({sysDefects.length})
                  </div>
                  <div className="space-y-2">
                    {sysDefects.map((d) => {
                      const ps = PRIORITY_STYLE[d.priority] || PRIORITY_STYLE.mittel;
                      return (
                        <div key={d.id} className="rounded-lg border border-white/10 p-3"
                             style={{ background: "rgba(255,255,255,0.025)" }}
                             data-testid={`hps-defect-${d.id}`}>
                          <div className="flex items-start gap-2.5">
                            <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: ps.dot }} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-sm text-white font-medium">{d.title}</span>
                                <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${ps.bg}`}>{ps.label}</span>
                              </div>
                              {d.description && (
                                <div className="text-xs text-white/65 leading-relaxed">{d.description}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* KI-Bewertung */}
              {sysAi && (
                <div className="rounded-lg border border-blue-400/25 p-3 mb-4"
                     style={{ background: "linear-gradient(135deg, rgba(74,158,255,0.06), rgba(37,99,235,0.03))" }}
                     data-testid={`hps-ai-${sys.id}`}>
                  <div className="text-[11px] uppercase tracking-wider text-blue-200 font-medium mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> KI-Bewertung
                  </div>
                  <div className="space-y-2 text-sm text-white/80">
                    <div>
                      <span className="text-[11px] uppercase tracking-wider text-white/40">Zustand</span>
                      <p className="leading-relaxed mt-0.5">{sysAi.condition_text}</p>
                    </div>
                    <div>
                      <span className="text-[11px] uppercase tracking-wider text-white/40">Risikoeinschätzung</span>
                      <p className="leading-relaxed mt-0.5">{sysAi.risk_assessment}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] uppercase tracking-wider text-white/40">Empfohlene Priorität:</span>
                      {(() => {
                        const ps = PRIORITY_STYLE[sysAi.priority_recommendation] || PRIORITY_STYLE.mittel;
                        return <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${ps.bg}`}>{ps.label}</span>;
                      })()}
                    </div>
                    {sysAi.missing_info?.length > 0 && (
                      <div className="rounded-md bg-amber-500/10 border border-amber-400/20 p-2 mt-2">
                        <div className="text-[11px] text-amber-200 flex items-center gap-1.5 mb-1">
                          <Info className="w-3 h-3" /> Hinweis auf fehlende Informationen
                        </div>
                        <ul className="list-disc pl-5 text-xs text-amber-100/80 space-y-0.5">
                          {sysAi.missing_info.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Sanierungsvorschläge */}
              {sysProposals.length > 0 && (
                <div className="mb-3">
                  <div className="text-[11px] uppercase tracking-wider text-white/40 font-medium mb-2 flex items-center gap-1.5">
                    <Wrench className="w-3.5 h-3.5" /> Sanierungsvorschläge
                  </div>
                  <div className="space-y-2">
                    {sysProposals.map((p) => (
                      <div key={p.id} className="rounded-lg border border-emerald-400/20 p-3"
                           style={{ background: "rgba(34,197,94,0.04)" }}
                           data-testid={`hps-proposal-${p.id}`}>
                        <div className="text-sm text-white font-medium">{p.title}</div>
                        {p.description && <div className="text-xs text-white/65 mt-1 leading-relaxed">{p.description}</div>}
                        {p.estimated_cost_range && (
                          <div className="text-xs text-emerald-300 mt-2 font-medium">{p.estimated_cost_range}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fotos */}
              {sysPhotos.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-white/40 font-medium mb-2 flex items-center gap-1.5">
                    <ImageIcon className="w-3.5 h-3.5" /> Fotos
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {sysPhotos.map((ph) => (
                      <div key={ph.id} className="aspect-video rounded-lg border border-dashed border-white/15 flex items-center justify-center text-xs text-white/35"
                           style={{ background: "rgba(255,255,255,0.02)" }}>
                        <div className="text-center px-2">
                          <ImageIcon className="w-5 h-5 mx-auto mb-1 opacity-50" />
                          <div className="text-[10px]">Platzhalter</div>
                          <div className="text-[10px] text-white/30 mt-0.5 line-clamp-2">{ph.caption}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bericht-Vorschau */}
      {report && (
        <Link to={`/app/hps-anlagencheck/bericht/${report.id}`} data-testid="hps-report-link"
              className="block rounded-xl border border-blue-400/30 p-5 hover:bg-blue-500/5 transition-all"
              style={{ background: "linear-gradient(135deg, rgba(74,158,255,0.06), rgba(37,99,235,0.02))" }}>
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-300" />
            <div className="flex-1">
              <div className="text-sm font-medium text-white">Berichtsvorschau anzeigen</div>
              <div className="text-xs text-white/55 mt-0.5">{report.title}</div>
            </div>
            <span className="text-xs text-amber-200 bg-amber-500/15 border border-amber-400/30 rounded px-2 py-1">
              {report.status === "in_pruefung" ? "fachlich zu prüfen" : report.status}
            </span>
          </div>
        </Link>
      )}

      {/* Footnote Disclaimer */}
      <div className="rounded-lg border border-amber-400/20 bg-amber-500/10 p-3 text-xs text-amber-100/90 flex items-start gap-2">
        <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          {data.ki_disclaimer || "Die KI unterstützt Bewertung und Dokumentation. Die finale fachliche Freigabe erfolgt durch eine verantwortliche Elektrofachkraft oder HPS."}
        </span>
      </div>
    </div>
  );
}


function PriorityTile({ color, label, count, testid }) {
  return (
    <div className="rounded-xl border border-white/10 p-4 text-center" data-testid={testid}
         style={{ background: "rgba(255,255,255,0.04)" }}>
      <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ background: color }} />
      <div className="text-2xl font-semibold text-white">{count}</div>
      <div className="text-[11px] uppercase tracking-wider text-white/50 mt-1">{label}</div>
    </div>
  );
}
